/*
 * ============================================================
 * SAV_ERP — Smart Account Vision Enterprise Resource Planning
 * Copyright (c) 2026 Hatem Mohamed Abdalla.
 * All rights reserved.
 *
 * This file is part of a proprietary software product owned
 * solely by Hatem Mohamed Abdalla (UAE, +971 50 390 9781).
 * Copying, redistribution, modification, reverse engineering
 * or reuse in any form without prior written consent of the
 * copyright owner is strictly prohibited and will be
 * prosecuted under UAE Federal Law No. 38/2021 on Copyright
 * and applicable international treaties.
 *
 * Fingerprint: SAV-ERP-HMA-2026-#a7f3d0
 * ============================================================
 */
const LEGACY_PRIMARY_AUTH_TOKEN_KEY = 'authToken';
const LEGACY_AUTH_TOKEN_KEYS = [LEGACY_PRIMARY_AUTH_TOKEN_KEY, 'auth_token', 'token'];
const AUTH_CONTEXT_KEY = 'sav_erp_auth_context';
const CONTEXT_TOKEN_KEYS = {
  erp: 'sav_erp_erp_auth_token',
  superadmin: 'sav_erp_superadmin_auth_token',
};

const normalizeContext = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return raw === 'superadmin' || raw === 'super_admin' ? 'superadmin' : 'erp';
};

const buildSessionExpiredKey = (context) => `sessionExpired:${normalizeContext(context)}`;

const getStorage = (type) => {
  try {
    if (type === 'session' && typeof sessionStorage !== 'undefined') return sessionStorage;
    if (type === 'local' && typeof localStorage !== 'undefined') return localStorage;
  } catch (_error) {
    return null;
  }
  return null;
};

const readKey = (key) => {
  const session = getStorage('session');
  const local = getStorage('local');
  try {
    const sessionValue = session?.getItem(key) || '';
    if (sessionValue) return sessionValue;
    const localValue = local?.getItem(key) || '';
    if (localValue) {
      session?.setItem(key, localValue);
      local?.removeItem(key);
      return localValue;
    }
    return '';
  } catch (_error) {
    return '';
  }
};

const writeKey = (key, value) => {
  try {
    getStorage('session')?.setItem(key, value);
    getStorage('local')?.removeItem(key);
  } catch (_error) {
    // ignore storage failures and keep app responsive
  }
};

const removeKey = (key) => {
  try {
    getStorage('session')?.removeItem(key);
    getStorage('local')?.removeItem(key);
  } catch (_error) {
    // ignore storage failures
  }
};

const getTokenKeyForContext = (context) => CONTEXT_TOKEN_KEYS[normalizeContext(context)] || CONTEXT_TOKEN_KEYS.erp;

export const setAuthContext = (context) => {
  const normalized = normalizeContext(context);
  try {
    getStorage('session')?.setItem(AUTH_CONTEXT_KEY, normalized);
    getStorage('local')?.removeItem(AUTH_CONTEXT_KEY);
  } catch (_error) {
    // ignore storage failures
  }
  return normalized;
};

export const getAuthContext = () => {
  const stored = readKey(AUTH_CONTEXT_KEY);
  return normalizeContext(stored || 'erp');
};

const migrateLegacyToken = (context) => {
  const normalized = normalizeContext(context);
  for (const legacyKey of LEGACY_AUTH_TOKEN_KEYS) {
    const legacyToken = readKey(legacyKey);
    if (legacyToken) {
      writeKey(getTokenKeyForContext(normalized), legacyToken);
      LEGACY_AUTH_TOKEN_KEYS.forEach(removeKey);
      return legacyToken;
    }
  }
  return '';
};

export const getAuthToken = (context = null) => {
  const normalized = normalizeContext(context || getAuthContext());
  const token = readKey(getTokenKeyForContext(normalized));
  if (token) {
    writeKey(getTokenKeyForContext(normalized), token);
    return token;
  }
  return migrateLegacyToken(normalized);
};

export const persistAuthToken = (token, context = null) => {
  if (!token) return;
  const normalized = setAuthContext(context || getAuthContext());
  writeKey(getTokenKeyForContext(normalized), token);
  removeKey(buildSessionExpiredKey(normalized));
};

export const clearAuthStorage = (context = null) => {
  const normalized = normalizeContext(context || getAuthContext());
  removeKey(getTokenKeyForContext(normalized));
  removeKey(buildSessionExpiredKey(normalized));
};

export const clearAllAuthStorage = () => {
  Object.values(CONTEXT_TOKEN_KEYS).forEach(removeKey);
  LEGACY_AUTH_TOKEN_KEYS.forEach(removeKey);
  removeKey(buildSessionExpiredKey('erp'));
  removeKey(buildSessionExpiredKey('superadmin'));
  removeKey(AUTH_CONTEXT_KEY);
};

export const markSessionExpired = (context = null) => {
  const normalized = normalizeContext(context || getAuthContext());
  writeKey(buildSessionExpiredKey(normalized), '1');
};

export const clearSessionExpired = (context = null) => {
  const normalized = normalizeContext(context || getAuthContext());
  removeKey(buildSessionExpiredKey(normalized));
};

export const isSessionExpired = (context = null) => {
  const normalized = normalizeContext(context || getAuthContext());
  return readKey(buildSessionExpiredKey(normalized)) === '1';
};
